import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { API_BASE_URL, fetchJson } from './api';
import {
    AnalyticsMetricCard,
    AnalyticsPanelSection,
    analyticsBodyCellSx,
    analyticsHeadCellSx,
    analyticsTableSx,
    glassSubCardSx
} from './analyticsSurface';

function getAuthHeader() {
    try {
        const token = localStorage.getItem('monsurv_auth_token') || '';
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function clampPct(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.min(100, Math.round(x)));
}

function pctColor(pct) {
    if (pct >= 90) return '#b91c1c';
    if (pct >= 75) return '#b45309';
    return '#047857';
}

function WorkloadBar({ pct }) {
    const p = clampPct(pct);
    const fg = pctColor(p);
    return (
        <Box sx={{ minWidth: 150 }}>
            <Box sx={{ height: 10, background: alpha('#0f172a', 0.08), borderRadius: 999, overflow: 'hidden' }}>
                <Box sx={{ height: 10, width: `${p}%`, background: fg }} />
            </Box>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.45 }}>{p}%</Typography>
        </Box>
    );
}

function SimpleTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#ffffff', 0.72)}`, background: 'rgba(255,255,255,0.96)', boxShadow: '0 18px 34px rgba(15, 23, 42, 0.12)', p: 1.1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 12, mb: 0.35 }}>{label}</Typography>
            {payload.map((entry) => (
                <Typography key={entry.dataKey} sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {entry.name || entry.dataKey}: <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>{entry.value}</Box>
                </Typography>
            ))}
        </Box>
    );
}

export default function Workload({ roleMode = 'surveyor', isAdmin = false }) {
    const [horizonDays, setHorizonDays] = useState(30);
    const [onlyOverCap, setOnlyOverCap] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [surveyors, setSurveyors] = useState([]);

    const [rules, setRules] = useState(null);
    const [weights, setWeights] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsError, setSettingsError] = useState('');
    const [settingsSaving, setSettingsSaving] = useState(false);

    const WEIGHT_KEYS = useMemo(() => ([
        { key: 'BASE_PROJECT', label: 'Base points per active project' },
        { key: 'RISK_GREEN', label: 'Risk points (GREEN)' },
        { key: 'RISK_AMBER', label: 'Risk points (AMBER)' },
        { key: 'RISK_RED', label: 'Risk points (RED)' },
        { key: 'DEADLINE_30D', label: 'Deadline points (≤30d)' },
        { key: 'DEADLINE_14D', label: 'Deadline points (≤14d)' },
        { key: 'DEADLINE_7D', label: 'Deadline points (≤7d)' },
        { key: 'BOOKING_SLOT_30MIN', label: 'Booking points per 30min slot' }
    ]), []);

    async function loadSettings() {
        setSettingsLoading(true);
        setSettingsError('');
        try {
            const [ruleJson, weightJson] = await Promise.all([
                fetchJson(`${API_BASE_URL}/api/workload/rules`, { method: 'GET', headers: { ...getAuthHeader() } }),
                fetchJson(`${API_BASE_URL}/api/workload/weights`, { method: 'GET', headers: { ...getAuthHeader() } })
            ]);
            setRules(ruleJson?.rules || null);
            setWeights(weightJson?.weights || null);
        } catch (e) {
            setRules(null);
            setWeights(null);
            setSettingsError(String(e?.message || 'Failed to load workload settings'));
        } finally {
            setSettingsLoading(false);
        }
    }

    async function saveRules() {
        if (!rules || !isAdmin) return;
        setSettingsSaving(true);
        setSettingsError('');
        try {
            const out = await fetchJson(`${API_BASE_URL}/api/workload/rules`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ rules })
            });
            setRules(out?.rules || rules);
        } catch (e) {
            setSettingsError(String(e?.message || 'Failed to save rules'));
        } finally {
            setSettingsSaving(false);
        }
    }

    async function saveWeights() {
        if (!weights || !isAdmin) return;
        setSettingsSaving(true);
        setSettingsError('');
        try {
            const out = await fetchJson(`${API_BASE_URL}/api/workload/weights`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ weights })
            });
            setWeights(out?.weights || weights);
            await load();
        } catch (e) {
            setSettingsError(String(e?.message || 'Failed to save weights'));
        } finally {
            setSettingsSaving(false);
        }
    }

    async function load() {
        setLoading(true);
        setError('');
        try {
            const data = await fetchJson(`${API_BASE_URL}/api/workload/overview?horizon_days=${encodeURIComponent(String(horizonDays))}`, {
                method: 'GET',
                headers: { ...getAuthHeader() }
            });
            setSurveyors(Array.isArray(data?.surveyors) ? data.surveyors : []);
        } catch (e) {
            setError(String(e?.message || 'Failed to load workload'));
            setSurveyors([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [horizonDays]);

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rows = useMemo(() => {
        const list = Array.isArray(surveyors) ? surveyors : [];
        const filtered = onlyOverCap
            ? list.filter((surveyor) => {
                const overPoints = Number(surveyor.total_points || 0) > Number(surveyor.weekly_capacity_points || 0);
                const overPct = Number(surveyor.utilisation_pct || 0) >= 100;
                const overProjects = Number(surveyor.active_project_count || 0) >= Number(surveyor.max_concurrent_projects || 0);
                return overPoints || overPct || overProjects;
            })
            : list;
        return filtered.slice().sort((a, b) => Number(b.utilisation_pct || 0) - Number(a.utilisation_pct || 0));
    }, [surveyors, onlyOverCap]);

    const totalCount = rows.length;
    const over90Count = rows.filter((row) => Number(row.utilisation_pct || 0) >= 90).length;
    const totalActiveProjects = rows.reduce((sum, row) => sum + Number(row.active_project_count || 0), 0);
    const avgUtilisation = totalCount ? Math.round(rows.reduce((sum, row) => sum + Number(row.utilisation_pct || 0), 0) / totalCount) : 0;
    const chartData = rows.slice(0, 6).map((row) => ({
        name: String(row?.name || '').trim() || 'Surveyor',
        utilisation: clampPct(row?.utilisation_pct),
        activeProjects: Number(row?.active_project_count || 0)
    }));

    return (
        <Box sx={{ display: 'grid', gap: 2.5, mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
                <AnalyticsMetricCard
                    icon={<EngineeringRoundedIcon fontSize="small" />}
                    label="Surveyors"
                    value={String(totalCount)}
                    caption="People visible in the current capacity view."
                    accent="linear-gradient(90deg, rgba(36, 50, 66, 0.95), rgba(59, 130, 246, 0.62))"
                />
                <AnalyticsMetricCard
                    icon={<WarningAmberRoundedIcon fontSize="small" />}
                    label="90%+ Utilised"
                    value={String(over90Count)}
                    caption="Delivery pressure hotspots that may need intervention soon."
                    accent="linear-gradient(90deg, rgba(153, 27, 27, 0.92), rgba(244, 63, 94, 0.56))"
                />
                <AnalyticsMetricCard
                    icon={<PlaylistAddCheckRoundedIcon fontSize="small" />}
                    label="Active Projects"
                    value={String(totalActiveProjects)}
                    caption="Total live projects assigned across visible surveyors."
                    accent="linear-gradient(90deg, rgba(15, 118, 110, 0.9), rgba(45, 212, 191, 0.54))"
                />
                <AnalyticsMetricCard
                    icon={<InsightsRoundedIcon fontSize="small" />}
                    label="Average Utilisation"
                    value={`${avgUtilisation}%`}
                    caption={`Capacity view over the next ${horizonDays} days for ${roleMode}.`}
                    accent="linear-gradient(90deg, rgba(180, 83, 9, 0.9), rgba(245, 158, 11, 0.56))"
                />
            </Box>

            <AnalyticsPanelSection
                eyebrow="Capacity Controls"
                title="Shape the workload lens"
                right={<Chip size="small" label={onlyOverCap ? 'Over-capacity only' : 'All visible surveyors'} sx={{ fontWeight: 800, backgroundColor: alpha('#eef4fb', 0.9) }} />}
            >
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ lg: 'center' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Horizon</InputLabel>
                            <Select label="Horizon" value={horizonDays} onChange={(e) => setHorizonDays(Number(e.target.value))}>
                                <MenuItem value={14}>14 days</MenuItem>
                                <MenuItem value={30}>30 days</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={<Checkbox checked={onlyOverCap} onChange={(e) => setOnlyOverCap(e.target.checked)} />}
                            label="Show only over-capacity"
                        />
                    </Stack>
                    <Button variant="contained" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Button>
                </Stack>
            </AnalyticsPanelSection>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.15fr 1fr' }, gap: 2.5 }}>
                <AnalyticsPanelSection
                    eyebrow="Bottlenecks"
                    title="Utilisation pressure"
                >
                    <Box sx={{ ...glassSubCardSx, minHeight: 300 }}>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={chartData} margin={{ left: 8, right: 8 }}>
                                <CartesianGrid strokeDasharray="3 4" vertical={false} stroke="rgba(148, 163, 184, 0.28)" />
                                <XAxis dataKey="name" tickLine={false} axisLine={false} />
                                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                                <Tooltip content={<SimpleTooltip />} />
                                <Bar dataKey="utilisation" name="Utilisation %" fill="#2f80ed" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </AnalyticsPanelSection>

                <AnalyticsPanelSection
                    eyebrow="Operational Readout"
                    title="What to watch next"
                >
                    <Stack spacing={1.2}>
                        <Box sx={glassSubCardSx}>
                            <Typography sx={{ fontWeight: 800 }}>Current focus</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.4 }}>
                                Prioritise surveyors above 90% utilisation or at maximum concurrent projects before new assignments are issued.
                            </Typography>
                        </Box>
                        <Box sx={glassSubCardSx}>
                            <Typography sx={{ fontWeight: 800 }}>Settings mode</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.4 }}>
                                Workload modelling settings remain available below, but they now sit behind a secondary expandable area rather than competing with daily operational triage.
                            </Typography>
                        </Box>
                        <Box sx={glassSubCardSx}>
                            <Typography sx={{ fontWeight: 800 }}>Role access</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.4 }}>
                                This tab remains an operational view for Admin and Surveyor roles. Settings edits are restricted to Admin.
                            </Typography>
                        </Box>
                    </Stack>
                </AnalyticsPanelSection>
            </Box>

            <AnalyticsPanelSection
                eyebrow="Workload Register"
                title="Surveyor capacity breakdown"
            >
                {error ? <Typography sx={{ color: '#b91c1c', fontSize: 13, mb: 1 }}>{error}</Typography> : null}
                <TableContainer sx={{ borderRadius: 4, border: `1px solid ${alpha('#0f172a', 0.06)}`, backgroundColor: alpha('#ffffff', 0.56) }}>
                    <Table sx={{ ...analyticsTableSx, minWidth: 1080 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 220 }}>Surveyor</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 90 }}>Level</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 130, textAlign: 'right' }}>Active Projects</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 220 }}>Utilisation</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 170, textAlign: 'right' }}>Points / Capacity</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 160, textAlign: 'right' }}>Booked Slots</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 140, textAlign: 'right' }}>Deadlines 7d</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 140, textAlign: 'right' }}>RED Exposure</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 130, textAlign: 'right' }}>Nearest Due</TableCell>
                                <TableCell sx={{ ...analyticsHeadCellSx, width: 150, textAlign: 'right' }}>Risk Mix</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} sx={{ ...analyticsBodyCellSx, color: 'text.secondary' }}>{loading ? 'Loading…' : 'No surveyors to show.'}</TableCell>
                                </TableRow>
                            ) : rows.map((surveyor) => {
                                const pct = clampPct(surveyor.utilisation_pct);
                                const over = pct >= 90 || Number(surveyor.active_project_count || 0) >= Number(surveyor.max_concurrent_projects || 0);
                                return (
                                    <TableRow key={surveyor.surveyor_id} hover sx={{ backgroundColor: over ? alpha('#fff7ed', 0.82) : 'transparent' }}>
                                        <TableCell sx={analyticsBodyCellSx}>
                                            <Typography sx={{ fontWeight: 800 }}>{surveyor.name}</Typography>
                                        </TableCell>
                                        <TableCell sx={analyticsBodyCellSx}>{surveyor.role_level || '—'}</TableCell>
                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'right' }}>
                                            {surveyor.active_project_count ?? 0}
                                            <Typography component="span" sx={{ color: 'text.secondary', fontSize: 12 }}> / {surveyor.max_concurrent_projects ?? '—'}</Typography>
                                        </TableCell>
                                        <TableCell sx={analyticsBodyCellSx}><WorkloadBar pct={pct} /></TableCell>
                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'right' }}>{surveyor.total_points ?? 0} / {surveyor.weekly_capacity_points ?? 0}</TableCell>
                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'right' }}>{surveyor.booked_slots_14d ?? 0}</TableCell>
                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'right' }}>{surveyor.open_deadlines_7d ?? 0}</TableCell>
                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'right' }}>{surveyor.risk_exposure_red_count ?? 0}</TableCell>
                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'right' }}>{surveyor.nearest_due_days_min === null || surveyor.nearest_due_days_min === undefined ? '—' : surveyor.nearest_due_days_min}</TableCell>
                                        <TableCell sx={{ ...analyticsBodyCellSx, textAlign: 'right' }}>
                                            {surveyor.active_risk_red_count === null || surveyor.active_risk_red_count === undefined
                                                ? '—'
                                                : `${surveyor.active_risk_red_count ?? 0}/${surveyor.active_risk_amber_count ?? 0}/${surveyor.active_risk_green_count ?? 0}`}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography sx={{ mt: 1.2, fontSize: 12, color: 'text.secondary' }}>
                    Notes: bookings and deadline indicators integrate when supporting tables exist; otherwise they fall back to zero.
                </Typography>
            </AnalyticsPanelSection>

            <Accordion
                disableGutters
                sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f6f9fc', 0.8)} 100%)`,
                    border: `1px solid ${alpha('#ffffff', 0.72)}`,
                    boxShadow: '0 22px 44px rgba(15, 23, 42, 0.08)',
                    '&::before': { display: 'none' }
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 2, sm: 2.5 }, minHeight: 74 }}>
                    <Box>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>Admin Model Controls</Typography>
                        <Typography sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Workload settings</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Secondary configuration for recommendation rules and utilisation weights.</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 } }}>
                    {settingsError ? <Typography sx={{ color: '#b91c1c', fontSize: 13, mb: 1.2 }}>{settingsError}</Typography> : null}
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
                        Saving remains Admin-only. Surveyors can review the current model, but cannot change it.
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                        <Box sx={glassSubCardSx}>
                            <Typography sx={{ fontWeight: 800, mb: 1.2 }}>Recommendation rules</Typography>
                            {rules ? (
                                <Stack spacing={1.2}>
                                    <TextField size="small" label="Top N" type="number" value={rules.top_n ?? 3} onChange={(e) => setRules({ ...rules, top_n: Number(e.target.value) })} />
                                    <TextField size="small" label="Booking horizon (days)" type="number" value={rules.booking_horizon_days ?? 14} onChange={(e) => setRules({ ...rules, booking_horizon_days: Number(e.target.value) })} />
                                    <TextField size="small" label="Deadline congestion threshold (7d)" type="number" value={rules.deadlines_congestion_threshold_7d ?? 3} onChange={(e) => setRules({ ...rules, deadlines_congestion_threshold_7d: Number(e.target.value) })} />
                                    <TextField size="small" label="Congestion penalty" type="number" value={rules.upcoming_deadlines_penalty ?? 10} onChange={(e) => setRules({ ...rules, upcoming_deadlines_penalty: Number(e.target.value) })} />
                                    <TextField size="small" label="RED fit bonus (Senior)" type="number" value={rules.risk_fit_bonus_senior_red ?? 10} onChange={(e) => setRules({ ...rules, risk_fit_bonus_senior_red: Number(e.target.value) })} />
                                    <Typography sx={{ fontWeight: 700, fontSize: 13, mt: 0.6 }}>Seniority bonus</Typography>
                                    <TextField size="small" label="Senior" type="number" value={rules.seniority_bonus?.Senior ?? 10} onChange={(e) => setRules({ ...rules, seniority_bonus: { ...(rules.seniority_bonus || {}), Senior: Number(e.target.value) } })} />
                                    <TextField size="small" label="Mid" type="number" value={rules.seniority_bonus?.Mid ?? 5} onChange={(e) => setRules({ ...rules, seniority_bonus: { ...(rules.seniority_bonus || {}), Mid: Number(e.target.value) } })} />
                                    <TextField size="small" label="Junior" type="number" value={rules.seniority_bonus?.Junior ?? 0} onChange={(e) => setRules({ ...rules, seniority_bonus: { ...(rules.seniority_bonus || {}), Junior: Number(e.target.value) } })} />
                                    <Button variant="contained" onClick={saveRules} disabled={!isAdmin || settingsLoading || settingsSaving}>{settingsSaving ? 'Saving…' : 'Save rules'}</Button>
                                </Stack>
                            ) : (
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{settingsLoading ? 'Loading…' : 'No rules loaded.'}</Typography>
                            )}
                        </Box>

                        <Box sx={glassSubCardSx}>
                            <Typography sx={{ fontWeight: 800, mb: 1.2 }}>Weights</Typography>
                            {weights ? (
                                <Stack spacing={1.2}>
                                    {WEIGHT_KEYS.map((weight) => (
                                        <TextField
                                            key={weight.key}
                                            size="small"
                                            label={weight.label}
                                            type="number"
                                            value={weights[weight.key] ?? 0}
                                            onChange={(e) => setWeights({ ...weights, [weight.key]: Number(e.target.value) })}
                                        />
                                    ))}
                                    <Button variant="contained" onClick={saveWeights} disabled={!isAdmin || settingsLoading || settingsSaving}>{settingsSaving ? 'Saving…' : 'Save weights'}</Button>
                                </Stack>
                            ) : (
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{settingsLoading ? 'Loading…' : 'No weights loaded.'}</Typography>
                            )}
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}